import { ButtonDS } from "../Button";
import { DragAndDrop } from "../DragAndDrop";
import { Spacer } from "../Spacer";
import { triggerToast } from "../../../toast";
import { colors } from "../../../styles/figmaColors";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { ImportDisplayFile } from "./ImportDisplayFile";
import { ImportErrorCell, type ImportError } from "./ImportErrorCell";
import { ImportStepTitle } from "./ImportStepTitle";
import { downloadTemplate } from "./downloadTemplate";

export type ImportContentProps = {
  template: { fileName: string; columns: string[]; sheetName: string };
  validateRows: (rows: unknown[][]) => ImportError[];
  onSubmit: (file: File) => void;
  onCloseModal: () => void;
  errorTypeToLabel: (errorType: string) => string;
};

export const ImportContent = (props: ImportContentProps) => {
  const { t } = useTranslation();
  const [hasExtensionError, setHasExtensionError] = useState(false);
  const [errors, setErrors] = useState<ImportError[] | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isFileUploading, setIsFileUploading] = useState(false);

  const isValidImport = useMemo(
    () => errors !== null && errors.length === 0,
    [errors],
  );

  const MIME = {
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  };

  const checkFile = useCallback(
    (file: File) => {
      if (file.type !== MIME.xlsx) {
        setIsFileUploading(false);
        setHasExtensionError(true);
        setErrors(null);
      } else {
        setIsFileUploading(true);
        setHasExtensionError(false);
        setErrors(null);
        const fileReader = new FileReader();
        fileReader.readAsArrayBuffer(file);
        fileReader.onload = (event) => {
          try {
            const bufferArray = event.target?.result;
            if (!bufferArray) throw new Error("Invalid file");
            const workBook = XLSX.read(bufferArray, { type: "buffer" });
            const sheet = workBook.Sheets[workBook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
              header: 1,
              raw: false,
            });
            setErrors(props.validateRows(rows as unknown[][]));
          } catch {
            setErrors(null);
            triggerToast(t("trips.mime.type.failure"), "error");
          } finally {
            setIsFileUploading(false);
          }
        };
      }
    },
    [MIME.xlsx, props, t],
  );

  const clickToSubmit = useCallback(() => {
    if (!isValidImport || !selectedFile) return;
    props.onSubmit(selectedFile);
    props.onCloseModal();
  }, [isValidImport, selectedFile, props]);

  useEffect(() => {
    if (selectedFile) checkFile(selectedFile);
  }, [checkFile, selectedFile]);

  const handleDownloadTemplate = useCallback(
    () => downloadTemplate(props.template),
    [props.template],
  );

  const isFileAlreadyImported =
    errors !== null && !hasExtensionError && !isFileUploading;

  return (
    <>
      <ImportStepTitle step={1} title={t("trips.importPlanning.downloadModel")} />
      <Spacer y={2} />
      <ImportDisplayFile
        selectedFile={props.template.fileName}
        handleDownload={handleDownloadTemplate}
        type="DOWNLOAD"
      />
      <Spacer y={2} />
      <ImportStepTitle step={2} title={t("trips.importPlanning.importFile")} />
      <Spacer y={2} />
      {isFileAlreadyImported ? (
        <>
          <ImportDisplayFile
            selectedFile={selectedFile?.name}
            handleFileChange={setSelectedFile}
            type="REPLACE"
          />
          <Spacer y={1.5} />
          {errors.length > 0 && (
            <StyledFlex>
              <Spacer x={0.75} />
              <StyledColumn>
                <StyledCountErrors>
                  {errors.length}
                  {t(
                    `trips.importPlanning.${
                      errors.length === 1 ? "countError" : "countErrors"
                    }`,
                  )}
                </StyledCountErrors>
                <Spacer y={0.75} />
                <StyledCells>
                  {errors.map((error, index) => (
                    <>
                      <ImportErrorCell
                        error={error}
                        errorTypeToLabel={props.errorTypeToLabel}
                        key={"error" + index}
                      />
                      <Spacer y={0.5} />
                    </>
                  ))}
                </StyledCells>
              </StyledColumn>
              <Spacer x={0.75} />
            </StyledFlex>
          )}
          <Spacer y={1.5} />
          <ButtonDS
            text={t("vehicles.modal.addMultipleVehicles.add")}
            format={"hug"}
            buttonType={"primary"}
            onClick={clickToSubmit}
            disabled={!isValidImport}
          />
        </>
      ) : (
        <StyledFlexCenter>
          <DragAndDrop
            handleFileChange={setSelectedFile}
            body={t("vehicles.modal.addMultipleVehicles.dragAndDrop.subTitle")}
            hasExtensionError={hasExtensionError}
            isLoading={isFileUploading}
          />
        </StyledFlexCenter>
      )}
    </>
  );
};

const StyledFlex = styled.div`
  display: flex;
  width: 100%;
`;

const StyledFlexCenter = styled.div`
  display: flex;
  justify-content: center;
`;

const StyledColumn = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const StyledCountErrors = styled.div`
  color: ${colors["colors/system/error/error_normal"]};
`;

const StyledCells = styled.div`
  overflow-y: scroll;
  max-height: 20rem;
  width: 100%;
`;
